import type { MessageLogRow, PageResult } from '../types';
import { parseFraudScore } from '../../utils/fraudScore';

/** Raw row from GET /details/ or GET /details/{correlation_id}. */
export interface DetailsApiRow {
  message_id?: string | number | null;
  id?: string | number | null;
  correlation_id?: string | null;
  created_at?: string | null;
  created_timestamp?: string | null;
  phone?: string | null;
  phone_to?: string | null;
  phone_from?: string | null;
  to?: string | null;
  from_?: string | null;
  from?: string | null;
  status?: string | null;
  type?: string | null;
  message_type?: string | null;
  text?: string | null;
  message?: string | null;
  message_text?: string | null;
  message_encoded?: string | null;
  message_decoded?: string | null;
  fraud_score?: number | null;
  [key: string]: unknown;
}

export interface DetailsApiResponse {
  items?: DetailsApiRow[] | null;
  total?: number | null;
  limit?: number | null;
  offset?: number | null;
  mode?: string | null;
}

function str(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

export function mapDetailsRow(row: DetailsApiRow, index = 0): MessageLogRow {
  const correlation_id = str(row.correlation_id) || undefined;
  const created_at = str(row.created_at ?? row.created_timestamp) || undefined;
  const status = str(row.status) || undefined;
  const type = str(row.type ?? row.message_type) || undefined;
  const phone_to = str(row.phone_to ?? row.phone ?? row.to) || undefined;
  const phone_from = str(row.phone_from ?? row.from_ ?? row.from) || undefined;
  const id = row.message_id ?? row.id ?? correlation_id ?? `row-${index}`;
  return {
    ...row,
    id,
    __key: String(
      row.id ??
        row.message_id ??
        `${correlation_id ?? 'row'}-${index}-${created_at}-${status}-${type}`,
    ),
    created_at,
    correlation_id,
    type,
    phone: phone_to,
    phone_to,
    phone_from,
    status,
    fraud_score: parseFraudScore(row.fraud_score),
    text:
      str(
        row.message_decoded ??
          row.text ??
          row.message ??
          row.message_text ??
          row.message_encoded,
      ) || undefined,
  };
}

/** History rows from GET /details/{correlation_id}. */
export function mapDetailHistory(raw: unknown): MessageLogRow[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((r, i) => mapDetailsRow(r as DetailsApiRow, i));
  }
  if (typeof raw !== 'object') return [];
  const body = raw as DetailsApiResponse;
  if (Array.isArray(body.items)) {
    return body.items.map((r, i) => mapDetailsRow(r, i));
  }
  if (
    'correlation_id' in body ||
    'status' in body ||
    'created_at' in body ||
    'type' in body
  ) {
    return [mapDetailsRow(body as DetailsApiRow, 0)];
  }
  return [];
}

export function mapDetailsResponse(
  raw: unknown,
  fallback: { page: number; page_size: number },
): PageResult<MessageLogRow> {
  if (raw == null) {
    return { items: [], total: 0, page: fallback.page, page_size: fallback.page_size };
  }
  if (Array.isArray(raw)) {
    const items = raw.map((r, i) => mapDetailsRow(r as DetailsApiRow, i));
    return {
      items,
      total: items.length,
      page: fallback.page,
      page_size: fallback.page_size,
    };
  }
  if (typeof raw !== 'object') {
    return { items: [], total: 0, page: fallback.page, page_size: fallback.page_size };
  }
  const body = raw as DetailsApiResponse;
  const rows = Array.isArray(body.items) ? body.items : [];
  const items = rows.map(mapDetailsRow);
  const total = typeof body.total === 'number' ? body.total : items.length;
  const limit =
    typeof body.limit === 'number' && body.limit > 0 ? body.limit : fallback.page_size;
  const offset = typeof body.offset === 'number' ? body.offset : 0;
  const page = limit > 0 ? Math.floor(offset / limit) + 1 : fallback.page;
  return { items, total, page, page_size: limit };
}
